import {
  ODYSSEY_APP_ID,
  ODYSSEY_PROVIDER_ID,
  OdysseyConnectionConfigSchema,
  OdysseyStoredCredentialsSchema,
  type ConnectionProviderKey,
  type OdysseyConnectionConfig,
  type OdysseyStoredCredentials,
} from "@forgepoint/shared";

/** Raw Odyssey connection dialog state before validation. */
export interface OdysseyConnectionFormValues {
  baseUrl: string;
  apiKey: string;
  companyId: string;
}

/** The finalize request the Odyssey dialog submits once the form is valid. */
export interface OdysseyFinalizeInput {
  connectionProviderId: ConnectionProviderKey;
  appId: string;
  credentials: OdysseyStoredCredentials;
  config: OdysseyConnectionConfig;
}

/** Either a user-facing validation message or the finalize request to send. */
export type OdysseyConnectionFormResult =
  | { error: string }
  | { input: OdysseyFinalizeInput };

/** Placeholder shown in the base URL input. */
export const ODYSSEY_BASE_URL_PLACEHOLDER = "https://api.blinfo.com/<tenant>";

const MISSING_FIELDS_ERROR = "Provide the Odyssey base URL, API key and plant.";
const INVALID_BASE_URL_ERROR = `Enter a valid base URL, e.g. ${ODYSSEY_BASE_URL_PLACEHOLDER}.`;

/**
 * Reports whether every required Odyssey field has a value.
 * @param values - Raw form values
 * @returns True when the base URL, API key and plant are all filled in
 */
export function hasOdysseyRequiredFields(
  values: OdysseyConnectionFormValues,
): boolean {
  return Boolean(
    values.baseUrl.trim() && values.apiKey && values.companyId.trim(),
  );
}

/**
 * Validates the Odyssey connection form and builds the finalize request.
 * @param values - Raw form values
 * @returns A validation error, or the finalize input
 */
export function buildOdysseyConnectionPayload(
  values: OdysseyConnectionFormValues,
): OdysseyConnectionFormResult {
  if (!hasOdysseyRequiredFields(values)) {
    return { error: MISSING_FIELDS_ERROR };
  }
  const config = OdysseyConnectionConfigSchema.safeParse({
    baseUrl: values.baseUrl.trim(),
    companyId: values.companyId,
  });
  if (!config.success) {
    return { error: INVALID_BASE_URL_ERROR };
  }
  const credentials = OdysseyStoredCredentialsSchema.safeParse({
    type: "OdysseyServiceCredentials",
    apiKey: values.apiKey,
  });
  if (!credentials.success) {
    return { error: MISSING_FIELDS_ERROR };
  }
  return {
    input: {
      connectionProviderId: ODYSSEY_PROVIDER_ID,
      appId: ODYSSEY_APP_ID,
      credentials: credentials.data,
      config: config.data,
    },
  };
}
